/**
 * Live preview of the crest being extracted.
 *
 * Geometry is rebuilt from `crestPreviewAtom` and hung off a group at the
 * first vertex (floating origin), exactly as AlignmentOverlay does — Float32
 * positions at UTM scale jitter visibly otherwise.
 */
import { crestPreviewAtom } from '@/atoms/crestLine.atom';
import { useAtomValue } from 'jotai';
import { useEffect, useMemo } from 'react';
import * as THREE from 'three';

const PREVIEW_COLOR = 0xffc400;

export function CrestLinePreview() {
  const result = useAtomValue(crestPreviewAtom);

  const origin = useMemo(() => {
    const first = result?.runs[0]?.points;
    return first && first.length >= 3
      ? new THREE.Vector3(first[0], first[1], first[2])
      : new THREE.Vector3(0, 0, 0);
  }, [result]);

  const lines = useMemo(() => {
    if (!result) return [];
    const lineList: THREE.Line[] = [];
    for (const run of result.runs) {
      // Guard against degenerate runs: need at least 2 vertices (6 floats)
      // and a multiple of 3 for valid XYZ triplets.
      if (run.points.length < 6 || run.points.length % 3 !== 0) continue;

      const arr = new Float32Array(run.points.length);
      for (let i = 0; i < run.points.length / 3; i++) {
        arr[i * 3] = run.points[i * 3] - origin.x;
        arr[i * 3 + 1] = run.points[i * 3 + 1] - origin.y;
        arr[i * 3 + 2] = run.points[i * 3 + 2] - origin.z;
      }
      const geom = new THREE.BufferGeometry();
      geom.setAttribute('position', new THREE.BufferAttribute(arr, 3));
      const mat = new THREE.LineBasicMaterial({
        color: PREVIEW_COLOR,
        depthTest: false,
        transparent: true,
        opacity: 0.95,
      });
      lineList.push(new THREE.Line(geom, mat));
    }
    return lineList;
  }, [result, origin]);

  useEffect(() => {
    return () => {
      for (const line of lines) {
        line.geometry.dispose();
        (line.material as THREE.Material).dispose();
      }
    };
  }, [lines]);

  if (lines.length === 0) return null;
  return (
    <group position={origin}>
      {lines.map((l, i) => (
        <primitive key={i} object={l} />
      ))}
    </group>
  );
}
